import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import useGraphicsSearchResults from '@hooks/useGraphicsSearchResults';
import { ClickTarget, ContentType, click } from '@tracking';

import GraphicsSearchPageEmpty from './Empty';
import GraphicsSearchPageFeaturedArticle from './FeaturedArticle';
import GraphicsSearchPageHeader from './Header';
import LoadMoreButton from './LoadMoreButton';
import GraphicsSearchPagePaywall from './Paywall';
import GraphicsSearchPageResult from './Result';
import GraphicsSearchPageSearchBar from './Search';
import {
  LOAD_MORE_BUTTON_TEXT,
  MAX_INITIAL_PAGE_COUNT,
  PAYWALL_GRAPHICS,
  RESULTS_PER_PAGE,
} from './constants';
import type { GraphicsSearchPageProps, GraphicsSearchResults } from './interfaces';

const GraphicsSearchPageContent = ({
  currentPage = 1,
  dataArticle = null,
  initialCurrentUser,
  initialFilter,
  initialSearchQuery = '',
  pageCount = 1,
  paywallData,
  searchResults,
}: GraphicsSearchPageProps) => {
  const { trackEvent } = useEventTracking();
  // Initialize from props only so the server and client produce the same first
  // render. Any query/filter present in the URL but not mirrored into the props
  // is applied after hydration in the effect below, avoiding a mismatch.
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [filter, setFilter] = useState<string | undefined>(initialFilter);
  const currentPageRef = useRef<number>(currentPage);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!initialSearchQuery) {
      const queryParam = params.get('query');
      if (queryParam) setSearchQuery(queryParam);
    }
    if (!initialFilter) {
      const filterParam = params.get('filter');
      if (filterParam) setFilter(filterParam);
    }
    // Run once on mount to pick up URL params not passed as props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldShowPaywall = !initialCurrentUser?.is_subscriber;

  const { graphicsSearchResults, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGraphicsSearchResults({
      filter,
      limit: RESULTS_PER_PAGE,
      query: searchQuery,
      initialSearchResults: searchResults ?? [],
      initialCurrentPage: currentPage,
      initialPageCount: pageCount,
    });

  const prevSearchQueryProp = useRef(initialSearchQuery);
  const prevFilterProp = useRef(initialFilter);

  useEffect(() => {
    if (prevSearchQueryProp.current !== initialSearchQuery) {
      prevSearchQueryProp.current = initialSearchQuery;
      setSearchQuery(initialSearchQuery || '');
    }
    if (prevFilterProp.current !== initialFilter) {
      prevFilterProp.current = initialFilter;
      setFilter(initialFilter || undefined);
    }
  }, [initialSearchQuery, initialFilter]);

  const handleGraphicClick = (graphic: Omit<GraphicsSearchResults, 'paywallType'>) => {
    trackEvent(
      click({
        clickTarget: ClickTarget.GraphicClick,
        clickLink: graphic.url,
        contentId: Number(graphic.id),
        contentType: ContentType.Graphics,
      })
    );
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setSearchQuery(searchTerm);
  };

  const handleLoadMore = () => {
    if (!hasNextPage || isFetchingNextPage) return;

    fetchNextPage({ cancelRefetch: false });
    currentPageRef.current += 1;

    trackEvent(
      click({
        clickTarget: ClickTarget.Button,
        clickText: LOAD_MORE_BUTTON_TEXT,
        contentIndex: currentPage,
      })
    );
  };

  const sortedGraphics = useMemo(() => {
    if (!graphicsSearchResults?.length) return [];

    const graphics = [...graphicsSearchResults];
    // Split into left and right columns for two-column layout
    const leftColumn: GraphicsSearchResults[] = [];
    const rightColumn: GraphicsSearchResults[] = [];

    graphics.forEach((item, index) => {
      if (index % 2 === 0) {
        leftColumn.push(item);
      } else {
        rightColumn.push(item);
      }
    });

    // Concatenate: left column first, then right column
    // This makes CSS columns fill left-to-right, top-to-bottom
    return [...leftColumn, ...rightColumn];
  }, [graphicsSearchResults]);

  const noSearchResults = sortedGraphics?.length === 0;
  const pageCountLimit = initialSearchQuery === '' ? MAX_INITIAL_PAGE_COUNT : pageCount;

  return (
    <main className="relative">
      <GraphicsSearchPageHeader />
      <article className="relative mt-48 w-full bg-grayscale-beige/50 px-16 pb-36 pt-20 sm:px-28 sm:pt-32 md:pb-48 md:pt-36">
        <div
          className={clsx('mx-auto w-full max-w-container-lg', {
            'grid grid-cols-1 gap-36 border-0 md:grid-cols-4 lg:border-b lg:border-solid lg:border-grayscale-charcoal':
              !!dataArticle,
          })}
        >
          {dataArticle ? <GraphicsSearchPageFeaturedArticle dataArticle={dataArticle} /> : null}
          <GraphicsSearchPageSearchBar
            filter={filter}
            isDisabled={shouldShowPaywall}
            searchQuery={searchQuery}
            setSearchQuery={handleSearchTermChange}
            isCompact={!!dataArticle}
          />
        </div>
        <div
          className={clsx(
            'mx-auto mt-36 w-full max-w-container-lg columns-[1] gap-x-56 gap-y-32 sm:mt-64',
            {
              'sm:columns-[2]': !noSearchResults || shouldShowPaywall,
            }
          )}
        >
          {!noSearchResults && !shouldShowPaywall ? (
            sortedGraphics.map((graphic, index) => (
              <GraphicsSearchPageResult
                key={graphic.id}
                graphic={graphic}
                handleGraphicClick={handleGraphicClick}
                index={index}
                userIsPro={initialCurrentUser?.is_pro_subscriber}
              />
            ))
          ) : shouldShowPaywall ? (
            PAYWALL_GRAPHICS.map((graphic, index) => (
              <GraphicsSearchPageResult
                key={graphic.id}
                graphic={{
                  id: index,
                  imageUrl: graphic.graphic,
                  isPro: false,
                  publishDate: '',
                  title: '',
                  url: '',
                }}
                handleGraphicClick={graphic.handleGraphicClick}
                index={index}
                userIsPro={false}
              />
            ))
          ) : (
            <GraphicsSearchPageEmpty />
          )}
        </div>

        {hasNextPage && !isFetchingNextPage && pageCountLimit > currentPageRef.current ? (
          <LoadMoreButton handleLoadMore={handleLoadMore} />
        ) : isFetchingNextPage ? (
          <Spinner marginY="large" />
        ) : null}
      </article>
      <GraphicsSearchPagePaywall paywallData={paywallData} shouldShowPaywall={shouldShowPaywall} />
    </main>
  );
};

export default GraphicsSearchPageContent;
